import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Box, Button, CircularProgress, Tab, Tabs, Tooltip } from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { documentsApi, isCancelled } from './api';
import { apiError, hasAnyFilter } from './format';
import { useDocumentsList } from './useDocumentsList';
import DocumentsFilters from './DocumentsFilters';
import DocumentsTable from './DocumentsTable';
import DocumentCardDialog from './DocumentCardDialog';
import DocumentsExportDialog from './DocumentsExportDialog';
import WarningsPanel from './WarningsPanel';
import RulesDialog from './rules/RulesDialog';
import AnalyticsPanel from './analytics/AnalyticsPanel';
import { EMPTY_FILTERS } from './constants';
import { DocumentFilters, DocumentsMeta } from './types';

type PageTab = 'all' | 'warnings' | 'analytics';

/** «Документы»: список документов с умными фильтрами, карточка, проблемные документы на контроле, аналитика */
const DocumentsPage: React.FC = () => {
  const [meta, setMeta] = useState<DocumentsMeta | null>(null);
  const [metaError, setMetaError] = useState<{ text: string; forbidden: boolean } | null>(null);
  const [tab, setTab] = useState<PageTab>('all');
  const [openId, setOpenId] = useState<number | null>(null);
  const [warningsKey, setWarningsKey] = useState(0);
  const [analyticsKey, setAnalyticsKey] = useState(0);
  const [analyticsVisited, setAnalyticsVisited] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);

  const list = useDocumentsList(Boolean(meta));
  const { replaceFilters } = list;

  // переход из аналитики: список с фильтрами когорты (остальные фильтры сбрасываются)
  const openList = useCallback((patch: Partial<DocumentFilters>) => {
    replaceFilters({ ...EMPTY_FILTERS, ...patch });
    setTab('all');
  }, [replaceFilters]);

  useEffect(() => {
    if (tab === 'analytics') setAnalyticsVisited(true);
  }, [tab]);

  const loadMeta = useCallback((signal?: AbortSignal) => {
    return documentsApi.meta(signal)
      .then(res => { setMeta(res); setMetaError(null); })
      .catch(err => {
        if (isCancelled(err)) return;
        setMetaError({ text: apiError(err, 'Не удалось загрузить раздел «Документы»'), forbidden: err?.response?.status === 403 });
      });
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadMeta(controller.signal);
    return () => controller.abort();
  }, [loadMeta]);

  const refreshAll = () => {
    list.reload();
    setWarningsKey(k => k + 1);
    setAnalyticsKey(k => k + 1);
    loadMeta();
  };

  if (metaError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity={metaError.forbidden ? 'warning' : 'error'}>
          {metaError.forbidden ? 'У вас нет доступа к просмотру документов. Обратитесь к администратору.' : metaError.text}
        </Alert>
      </Box>
    );
  }

  if (!meta) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={32} /></Box>;
  }

  const { access } = meta;
  const showWarningsTab = Boolean(access.warningsScope);
  const showAnalyticsTab = Boolean(access.analyticsScope);
  const activeTab: PageTab = (tab === 'warnings' && showWarningsTab) || (tab === 'analytics' && showAnalyticsTab) ? tab : 'all';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {showWarningsTab || showAnalyticsTab ? (
          <Tabs
            value={activeTab} onChange={(_, v: PageTab) => setTab(v)} variant="scrollable" scrollButtons={false}
            sx={{ minHeight: 40, flex: '1 1 auto', '& .MuiTab-root': { textTransform: 'none', minHeight: 40, fontWeight: 600 } }}
          >
            <Tab value="all" label="Все документы" />
            {showWarningsTab && (
              <Tab
                value="warnings"
                label={
                  <Badge color="warning" badgeContent={meta.stats.warnings ?? 0} max={999} invisible={!meta.stats.warnings} sx={{ '& .MuiBadge-badge': { right: -14, top: 4 } }}>
                    Проблемные на контроле
                  </Badge>
                }
                sx={{ pr: 3 }}
              />
            )}
            {showAnalyticsTab && <Tab value="analytics" label="Аналитика" />}
          </Tabs>
        ) : <Box sx={{ flex: 1 }} />}

        {/* Кнопки переносятся одним блоком и прижаты вправо */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
          <Tooltip title="Обновить">
            <span>
              <Button size="small" variant="outlined" color="inherit" onClick={refreshAll} sx={{ minWidth: 36, borderColor: '#e2e8f0', height: 36 }} aria-label="Обновить">
                <RefreshRoundedIcon fontSize="small" />
              </Button>
            </span>
          </Tooltip>
          {activeTab === 'all' && access.export && (
            <Button
              size="small" variant="outlined" color="inherit" onClick={() => setExportOpen(true)} disabled={!list.pagination.total}
              startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
              sx={{ textTransform: 'none', borderColor: '#e2e8f0', height: 36 }}
            >
              Выгрузить
            </Button>
          )}
          {access.settingsScope && (
            <Tooltip title="Настройки: правила документов">
              <span>
                <Button size="small" variant="outlined" color="inherit" onClick={() => setRulesOpen(true)} sx={{ minWidth: 36, borderColor: '#e2e8f0', height: 36 }} aria-label="Настройки документов">
                  <SettingsOutlinedIcon fontSize="small" />
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      {activeTab === 'all' && (
        <>
          <DocumentsFilters
            meta={meta} filters={list.filters} facets={list.facets}
            patch={list.patchFilters} replace={list.replaceFilters} reset={list.resetFilters}
          />
          <DocumentsTable
            rows={list.rows} pagination={list.pagination} loading={list.loading} error={list.error}
            sort={list.sort} showAmount={access.docAmount} hasFilters={hasAnyFilter(list.filters)}
            onSort={list.toggleSort} onPage={list.setPage} onLimit={list.setLimit}
            onOpen={row => setOpenId(row.id)} onResetFilters={list.resetFilters}
          />
        </>
      )}
      {activeTab === 'warnings' && (
        <WarningsPanel
          refreshKey={warningsKey} onOpen={setOpenId}
          onChanged={() => { list.reload(); loadMeta(); }}
        />
      )}
      {/* аналитика остаётся смонтированной после первого открытия: при возврате графики не перезапрашиваются */}
      {showAnalyticsTab && analyticsVisited && (
        <Box sx={{ display: activeTab === 'analytics' ? 'block' : 'none' }}>
          <AnalyticsPanel meta={meta} active={activeTab === 'analytics'} reloadToken={analyticsKey} onOpenList={openList} />
        </Box>
      )}

      <DocumentCardDialog
        id={openId} access={access} onClose={() => setOpenId(null)}
        onChanged={() => { list.reload(); setWarningsKey(k => k + 1); loadMeta(); }}
      />

      {access.export && (
        <DocumentsExportDialog open={exportOpen} meta={meta} filters={list.effective} sort={list.sort} onClose={() => setExportOpen(false)} />
      )}

      {access.settingsScope && (
        <RulesDialog open={rulesOpen} onClose={() => setRulesOpen(false)} />
      )}
    </Box>
  );
};

export default DocumentsPage;
